import {
  CategoryHandler,
  CollectionHandler,
  getAlgoliaConfig,
  SearchFiltersHandler,
  SearchResultHandler,
  SequenceTypes,
  VersionHistoryHandler,
} from '@atlassian/mpac-services-client';

import type { BootstrapConfig } from '../application-config/types/BootstrapConfig';
import type {
  GetMarketplaceAppVersionHistoryQueryVariables,
  IOrchestrationConfig,
  IOrchestrationConfigRecord,
} from '@atlassian/mpac-services-client';

const getConfig = <T extends IOrchestrationConfigRecord>(config: IOrchestrationConfig<T>) => config;

/**
 * Orchestration configuration for DLA
 *
 * `config` accepts both a config and a function returning config. This can be used for returning
 *  configuration based on some feature flag or some custom logic.
 */
const config = (bootstrapConfig: BootstrapConfig) => {
  const algoliaConfig = getAlgoliaConfig(
    bootstrapConfig.appConfig.commonConfig.algoliaConfig,
    bootstrapConfig.featureFlags
  );

  return getConfig({
    GetMarketplaceSearch: {
      config: () => ({
        sequence: SequenceTypes.CUSTOM_ONLY,
        handler: new SearchResultHandler(),
      }),
    },
    GetMarketplaceFilters: {
      config: () => ({
        sequence: SequenceTypes.CUSTOM_ONLY,
        handler: new SearchFiltersHandler(),
      }),
    },
    GetCollection: {
      middlewaresConfig: {
        cache: {
          primary: {
            ttlMs: 60_000,
            cacheKey: ({ variables }) => variables.slug,
          },
        },
      },
      config: () => ({
        sequence: SequenceTypes.PRIMARY_FIRST,
        handler: new CollectionHandler(algoliaConfig),
      }),
    },
    GetMarketplaceCategory: {
      middlewaresConfig: {
        cache: {
          primary: {
            ttlMs: 60_000, // todo: update TTL
            cacheKey: ({ variables }) => variables.slug,
          },
        },
      },
      config: {
        sequence: SequenceTypes.PRIMARY_FIRST,
        handler: new CategoryHandler(algoliaConfig),
      },
    },
    // TODO: Update to rather use Apollo in memory cache which is more efficient
    GetMarketplaceAppVersionHistory: {
      middlewaresConfig: {
        cache: {
          primary: {
            ttlMs: 300_000,
            cacheKey: ({
              variables,
            }: {
              variables: GetMarketplaceAppVersionHistoryQueryVariables;
            }) =>
              `${variables.appId}-${variables.filters?.hosting ?? ''}-${variables.first ?? ''}-${
                variables.after ?? ''
              }`,
          },
        },
      },
      config: {
        sequence: SequenceTypes.PRIMARY_ONLY,
        handler: new VersionHistoryHandler(),
      },
    },
  });
};

export { config };
